// cspell:ignore cdoc cusins erwf exrate
// cspell:ignore inspcom inspdist inspexp inspexpc inspexpd inspfind inspfindr insphm inspnote
// cspell:ignore inspobs inspobsc inspobsh inspobsr inspq inspts inspunav invsub invl
// cspell:ignore acctm acctp attst clrw itra notif notifd notifgm payalloc pouta poutl subins

import { KSUID } from '@owpz/ksuid';

/**
 * Mapping of object types to their string prefixes used in AwpMarine IDs.
 * These prefixes make IDs human-readable and help identify the type of object.
 */
export const TYPE_PREFIX_MAPPING = {
  user: 'usr',
  account: 'acct',
  account_member: 'acctm',
  account_person: 'acctp',
  document: 'doc',
  attestation: 'attst',
  clearance_waiver: 'clrw',
  bank_account: 'bank',
  customer: 'cus',
  customer_document: 'cdoc',
  customer_inspector: 'cusins',
  operator: 'ope',
  vessel: 'ves',
  subcontractor_inspector: 'subins',
  // Documents that were inspector qualifications keep their IDs.
  inspector_qualification: 'inspq',
  inspector_unavailability: 'inspunav',
  inspector_comment: 'inspcom',
  inspection: 'insp',
  inspection_distribution: 'inspdist',
  inspection_note: 'inspnote',
  inspection_expense_comment: 'inspexpc',
  inspection_expense_document: 'inspexpd',
  inspection_report_observation: 'inspobs',
  inspection_report_observation_review: 'inspobsr',
  inspection_report_observation_comment: 'inspobsc',
  inspection_report_observation_highlight: 'inspobsh',
  inspection_report_highlight_month: 'insphm',
  inspection_report_finding: 'inspfind',
  inspection_report_finding_review: 'inspfindr',
  inspection_expense: 'inspexp',
  inspection_timesheet_entry: 'inspts',
  inspection_travel_risk_assessment: 'itra',
  invoice: 'inv',
  invoice_line: 'invl',
  invoice_submission: 'invsub',
  payment: 'pay',
  payment_allocation: 'payalloc',
  payout: 'pout',
  payout_line: 'poutl',
  payout_attempt: 'pouta',
  email_received_workflow: 'erwf',
  notification: 'notif',
  notification_delivery: 'notifd',
  notification_group_member: 'notifgm',
  exchange_rate: 'exrate',
  file: 'file',
  request_log: 'req',
  event: 'evt',
};

/** Valid object types that can be used in AwpMarine IDs */
export type AwpMarineObjectType = keyof typeof TYPE_PREFIX_MAPPING;

/** Array of all valid object types for validation purposes */
export const OBJECT_TYPES = Object.keys(TYPE_PREFIX_MAPPING) as AwpMarineObjectType[];

/**
 * AwpMarine ID utility class for creating and parsing typed identifiers.
 *
 * AwpMarine IDs follow the format: `{prefix}_{ksuid}`
 * - prefix: A short string indicating the object type (e.g., 'cus', 'cdoc')
 * - ksuid: A K-Sortable Unique Identifier providing temporal ordering
 *
 * Examples:
 * - cus_2SwapA1yZzBrhrq6dWRMZn0XkuY (customer) // cspell:disable-line
 * - cdoc_2SwapB2aAbCdefg7hWRMZn0XkuZ (customer_document)
 */
export class AwpMarineId {
  private constructor(
    private readonly _type: AwpMarineObjectType,
    private readonly _kid: string,
  ) {}

  /** The object type of this AwpMarine ID */
  get type() {
    return this._type;
  }

  /** The KSUID portion of this AwpMarine ID */
  get kid() {
    return this._kid;
  }

  /** Whether this AwpMarine ID represents an empty/null value */
  get isEmpty() {
    return this.kid === AwpMarineId.empty;
  }

  /**
   * Returns the string representation of this AwpMarine ID.
   * Format: `{prefix}_{ksuid}`
   */
  toString(): string {
    return `${TYPE_PREFIX_MAPPING[this.type]}_${this.kid.toString()}`;
  }

  /**
   * Creates an AwpMarine ID string from a type and existing KSUID.
   * @param type - The object type
   * @param kid - An existing KSUID string
   * @returns The formatted AwpMarine ID string
   */
  static create(type: AwpMarineObjectType, kid: string) {
    return new AwpMarineId(type, kid).toString();
  }

  /**
   * Generates a new random KSUID string without any type prefix.
   * @returns A new KSUID string
   */
  static generateKidOnly() {
    return KSUID.random().toString();
  }

  /**
   * Generates a new AwpMarine ID with a random KSUID.
   * @param type - The object type for the new ID
   * @returns A new AwpMarine ID string
   */
  static generate(type: AwpMarineObjectType) {
    return AwpMarineId.create(type, AwpMarineId.generateKidOnly());
  }

  /**
   * Parses a AwpMarine ID string into a AwpMarineId instance.
   * @param value - The AwpMarine ID string to parse (format: `{prefix}_{ksuid}`)
   * @returns A new AwpMarineId instance
   * @throws Error if the format is invalid or type is unrecognized
   */
  static parse(value: string) {
    const [type, id] = value.split('_');
    const mapped = Object.entries(TYPE_PREFIX_MAPPING).find(([, prefix]) => prefix === type)?.[0] as
      | AwpMarineObjectType
      | undefined;
    if (!mapped) throw new Error(`Invalid AwpMarine ID type: ${type}`);
    if (!id) throw new Error('Invalid AwpMarine ID format');
    return new AwpMarineId(mapped, id);
  }

  /**
   * Parses a AwpMarine ID string into a AwpMarineId instance, returning null if invalid.
   * @param value - The AwpMarine ID string to parse (format: `{prefix}_{ksuid}`)
   * @returns A new AwpMarineId instance, or null if parsing fails
   */
  static parseOrNil(value: string) {
    try {
      return AwpMarineId.parse(value);
    } catch {
      return null;
    }
  }
  /** A special KSUID representing an empty/null value (all zeros). Inlined rather than computed
   * via `KSUID.fromBytes(Buffer.alloc(20, 0))`: `Buffer` does not exist in browser bundles and
   * this initializer runs on import. */
  private static empty = '000000000000000000000000000';

  /**
   * Creates an empty AwpMarine ID for the given type.
   * Used to represent null/empty references while maintaining type information.
   * @param type - The object type for the empty ID
   * @returns An empty AwpMarine ID string
   */
  static fromEmpty(type: AwpMarineObjectType) {
    return AwpMarineId.create(type, AwpMarineId.empty);
  }

  /**
   * Validates whether a string is a valid AwpMarine ID.
   * @param value - The string to validate
   * @returns true if the string is a valid AwpMarine ID, false otherwise
   */
  static isValid(value: string) {
    try {
      AwpMarineId.parse(value);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Type guard to check if a string is a valid AwpMarineObjectType.
   * @param value - The string to check
   * @returns true if the value is a valid object type
   */
  static isValidType = (value: string): value is AwpMarineObjectType =>
    OBJECT_TYPES.includes(value as AwpMarineObjectType);
}
